/**
 * Receipt Exit Verification Page
 * 
 * Used by security personnel at the exit gate.
 * Scans customer receipts (barcode/QR) to verify purchases.
 * Flags duplicate receipts and alerts security.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import apiClient from '@/lib/api-client';
import {
    Shield, Barcode, CheckCircle, AlertTriangle, XCircle,
    Search, Clock, Package, ShoppingCart, User, ArrowLeft,
    Bell, BellOff,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';

interface ScanResult {
    status: 'valid' | 'already_used' | 'not_found';
    sale?: {
        id: string;
        orderNumber: string;
        grandTotal: string;
        customerName?: string;
        itemCount: number;
        scannedAt: string;
    };
    message: string;
}

export function ReceiptVerificationPage() {
    const navigate = useNavigate();
    const user = useAuthStore((s) => s.user);
    const addToast = useToastStore((s) => s.addToast);
    const inputRef = useRef<HTMLInputElement>(null);

    const [buffer, setBuffer] = useState('');
    const [lastKeyTime, setLastKeyTime] = useState(0);
    const [isScanning, setIsScanning] = useState(false);
    const [lastResult, setLastResult] = useState<ScanResult | null>(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [soundEnabled, setSoundEnabled] = useState(true);
    const [scanHistory, setScanHistory] = useState<ScanResult[]>([]);

    const SCAN_THRESHOLD = 80;

    // Keep input focused
    useEffect(() => {
        const focusInput = () => inputRef.current?.focus();
        focusInput();
        document.addEventListener('click', focusInput);
        return () => document.removeEventListener('click', focusInput);
    }, []);

    // Play alert sound
    const playSound = useCallback((type: 'valid' | 'duplicate' | 'not_found') => {
        if (!soundEnabled) return;
        try {
            const sounds: Record<string, string> = {
                valid: '/sounds/valid.mp3',
                duplicate: '/sounds/alert.mp3',
                not_found: '/sounds/error.mp3',
            };
            const audio = new Audio(sounds[type]);
            audio.volume = 0.7;
            audio.play().catch(() => { });
        } catch { }
    }, [soundEnabled]);

    // Process scan
    const processScan = useCallback(async (barcode: string) => {
        if (!barcode || barcode.length < 5 || isProcessing) return;

        setIsProcessing(true);
        setBuffer('');

        try {
            // Try to parse QR code data
            let saleId: string | undefined;
            let orderNumber: string | undefined;

            try {
                const qrData = JSON.parse(barcode);
                saleId = qrData.s;
                orderNumber = qrData.o;
            } catch {
                // Not QR code — treat as saleId or orderNumber
                if (barcode.length === 36) {
                    saleId = barcode;
                } else {
                    orderNumber = barcode;
                }
            }

            const { data } = await apiClient.post(
                `/tenants/${user!.tenantId}/receipt/verify`,
                { saleId, orderNumber }
            );

            const result: ScanResult = data?.data || data;
            setLastResult(result);
            setScanHistory(prev => [result, ...prev].slice(0, 20));
            playSound(result.status === 'already_used' ? 'duplicate' : result.status === 'not_found' ? 'not_found' : 'valid');

            if (result.status === 'already_used') {
                addToast({
                    type: 'error',
                    title: '⚠️ DUPLICATE RECEIPT!',
                    message: 'This receipt was already used. Alert security!',
                });
            }
        } catch {
            setLastResult({
                status: 'not_found',
                message: 'Error verifying receipt. Please try again.',
            });
        } finally {
            setIsProcessing(false);
            setTimeout(() => inputRef.current?.focus(), 200);
        }
    }, [user, isProcessing, playSound, addToast]);

    // Handle keyboard input (barcode scanner)
    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        const now = Date.now();
        const timeDiff = now - lastKeyTime;

        if (e.key === 'Enter') {
            e.preventDefault();
            if (buffer.length >= 5) {
                processScan(buffer.trim());
            }
            setBuffer('');
            setLastKeyTime(0);
            return;
        }

        if (e.key.length === 1) {
            if (timeDiff < SCAN_THRESHOLD && lastKeyTime > 0) {
                setIsScanning(true);
            }
            setLastKeyTime(now);
            setBuffer(prev => prev + e.key);
        }
    }, [buffer, lastKeyTime, processScan]);

    const formatNaira = (val: string) => `₦${parseFloat(val || '0').toLocaleString()}`;

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white">
            {/* Hidden input */}
            <input
                ref={inputRef}
                type="text"
                value={buffer}
                onKeyDown={handleKeyDown}
                onChange={() => { }}
                className="absolute opacity-0 w-0 h-0"
                autoComplete="off"
            />

            <div className="max-w-2xl mx-auto p-6 lg:p-8 space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <button onClick={() => navigate('/pos')} className="p-2 rounded-xl hover:bg-slate-800 transition-colors" type="button">
                            <ArrowLeft size={20} className="text-slate-400" />
                        </button>
                        <div className="p-3 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl shadow-lg">
                            <Shield size={24} className="text-white" />
                        </div>
                        <div>
                            <h1 className="text-xl font-bold">Exit Verification</h1>
                            <p className="text-sm text-slate-400">Scan customer receipts</p>
                        </div>
                    </div>
                    <button
                        onClick={() => setSoundEnabled(!soundEnabled)}
                        className={clsx('p-2 rounded-lg', soundEnabled ? 'bg-blue-500/20 text-blue-400' : 'bg-slate-700 text-slate-500')}
                        type="button"
                    >
                        {soundEnabled ? <Bell size={18} /> : <BellOff size={18} />}
                    </button>
                </div>

                {/* Scan Zone */}
                <Card className={clsx(
                    'border-2 transition-all',
                    isScanning ? 'border-emerald-400 bg-emerald-500/5' : 'border-slate-700 bg-slate-800/50'
                )}>
                    <CardContent className="p-12 text-center">
                        <motion.div
                            animate={isScanning ? { scale: [1, 1.05, 1] } : {}}
                            transition={{ duration: 0.5, repeat: isScanning ? Infinity : 0 }}
                        >
                            <Barcode size={64} className={clsx('mx-auto mb-6', isScanning ? 'text-emerald-400' : 'text-slate-600')} />
                        </motion.div>
                        <h2 className="text-xl font-bold mb-2">
                            {isProcessing ? 'Verifying...' : isScanning ? 'Reading Receipt...' : 'Ready to Scan'}
                        </h2>
                        <p className="text-sm text-slate-400">
                            {isProcessing ? <Spinner size="sm" /> : 'Scan the barcode or QR code on the customer\'s receipt'}
                        </p>
                        {buffer && (
                            <div className="mt-4 p-3 bg-slate-700 rounded-xl">
                                <p className="text-sm font-mono text-emerald-400">{buffer}</p>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Scan Result */}
                <AnimatePresence>
                    {lastResult && (
                        <motion.div
                            key={lastResult.sale?.id || 'result'}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                        >
                            <Card className={clsx(
                                'border-2',
                                lastResult.status === 'valid' ? 'border-emerald-400 bg-emerald-500/5' :
                                    lastResult.status === 'already_used' ? 'border-red-400 bg-red-500/5' :
                                        'border-amber-400 bg-amber-500/5'
                            )}>
                                <CardContent className="p-6">
                                    <div className="flex items-center gap-4">
                                        <div className={clsx(
                                            'w-16 h-16 rounded-2xl flex items-center justify-center',
                                            lastResult.status === 'valid' ? 'bg-emerald-500/20' :
                                                lastResult.status === 'already_used' ? 'bg-red-500/20' :
                                                    'bg-amber-500/20'
                                        )}>
                                            {lastResult.status === 'valid' ? <CheckCircle size={32} className="text-emerald-400" /> :
                                                lastResult.status === 'already_used' ? <XCircle size={32} className="text-red-400" /> :
                                                    <AlertTriangle size={32} className="text-amber-400" />}
                                        </div>
                                        <div className="flex-1">
                                            <h3 className={clsx('text-lg font-bold',
                                                lastResult.status === 'valid' ? 'text-emerald-400' :
                                                    lastResult.status === 'already_used' ? 'text-red-400' :
                                                        'text-amber-400'
                                            )}>
                                                {lastResult.status === 'valid' ? '✅ VALID RECEIPT' :
                                                    lastResult.status === 'already_used' ? '⚠️ DUPLICATE RECEIPT!' :
                                                        '❌ NOT FOUND'}
                                            </h3>
                                            <p className="text-sm text-slate-300 mt-1">{lastResult.message}</p>
                                        </div>
                                    </div>

                                    {lastResult.sale && (
                                        <div className="mt-4 grid grid-cols-3 gap-3">
                                            <div className="p-3 bg-slate-800 rounded-xl text-center">
                                                <p className="text-xs text-slate-400">Order</p>
                                                <p className="text-sm font-bold">{lastResult.sale.orderNumber}</p>
                                            </div>
                                            <div className="p-3 bg-slate-800 rounded-xl text-center">
                                                <p className="text-xs text-slate-400">Amount</p>
                                                <p className="text-sm font-bold">{formatNaira(lastResult.sale.grandTotal)}</p>
                                            </div>
                                            <div className="p-3 bg-slate-800 rounded-xl text-center">
                                                <p className="text-xs text-slate-400">Items</p>
                                                <p className="text-sm font-bold">{lastResult.sale.itemCount}</p>
                                            </div>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Recent Scans */}
                {scanHistory.length > 0 && (
                    <Card>
                        <CardContent className="p-4">
                            <h3 className="text-sm font-bold text-slate-400 mb-3 flex items-center gap-2">
                                <Clock size={14} /> Recent Scans ({scanHistory.length})
                            </h3>
                            <div className="space-y-2">
                                {scanHistory.slice(0, 10).map((scan, idx) => (
                                    <div key={idx} className={clsx(
                                        'flex items-center justify-between p-2 rounded-lg text-sm',
                                        scan.status === 'valid' ? 'bg-emerald-500/5' :
                                            scan.status === 'already_used' ? 'bg-red-500/5' : 'bg-slate-800'
                                    )}>
                                        <div className="flex items-center gap-2">
                                            {scan.status === 'valid' ? <CheckCircle size={14} className="text-emerald-400" /> :
                                                scan.status === 'already_used' ? <XCircle size={14} className="text-red-400" /> :
                                                    <AlertTriangle size={14} className="text-amber-400" />}
                                            <span className="text-slate-300">
                                                {scan.sale?.orderNumber || 'Unknown'}
                                            </span>
                                        </div>
                                        <span className="text-xs text-slate-500">
                                            {scan.sale?.grandTotal ? formatNaira(scan.sale.grandTotal) : ''}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                )}
            </div>
        </div>
    );
}